import type { z } from 'zod'
import { getAccessToken } from './accessToken'
import { ApiError } from './errors'

const parseBody = async (response: Response): Promise<unknown> => {
  const contentType = response.headers.get('content-type') ?? ''
  if (response.status === 204 || !contentType.includes('json')) return null
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

/**
 * `GET baseUrl + path` with the bearer token when signed in. The JSON reply is
 * validated against `schema`; a non-2xx status or a mismatch throws an
 * `ApiError`.
 */
export async function httpGet<T extends z.ZodTypeAny>(
  baseUrl: string,
  path: string,
  schema: T
): Promise<z.infer<T>> {
  const headers = new Headers({ Accept: 'application/json' })
  const accessToken = getAccessToken()
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`)

  const response = await fetch(baseUrl + path, {
    headers,
    credentials: 'same-origin',
  })
  const body = await parseBody(response)

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, body)
  }

  const parsed = schema.safeParse(body)
  if (!parsed.success) {
    throw new ApiError(
      response.status,
      'Response did not match expected schema',
      { issues: parsed.error.issues, body }
    )
  }
  return parsed.data
}
